<template>
  <div class="row q-col-gutter-x-xl q-col-gutter-y-sm">
    <div class="col-4">
      <PInput
        v-model.trim="formData.pincode"
        label="Pincode"
        placeholder="Enter pincode"
        required
        :disable="isGstRegistered || disabled"
        @update:model-value="onUpdatePincode"
      />
    </div>
    <div class="col-4">
      <PSelectInput
        v-model="formData.city"
        :options="unref(cityFilter.entities)"
        :loading="citiesLoading"
        label="City"
        :hint="!disabled ? 'Press enter to add new city' : ''"
        option-label="name"
        option-value="name"
        placeholder="Enter city"
        required
        :disable="disabled"
        new-value-mode="add-unique"
        @new-value="onCreateCity"
        @filter="cityFilter.onFilter"
        @filter-abort="cityFilter.onFilterAbort"
      />
    </div>
    <div class="col-4">
      <PSelectInput
        v-model="computedStateCode"
        :options="unref(stateFilter.entities)"
        label="State"
        option-label="name"
        option-value="state_code"
        placeholder="Select state"
        required
        :disable="isGstRegistered || disabled"
        @filter="stateFilter.onFilter"
        @filter-abort="stateFilter.onFilterAbort"
        @update:model-value="onUpdateState"
      />
    </div>
    <div class="col-12">
      <PInput
        v-model="computedAddress"
        :validations="{
          label: 'Address',
          maxLength: 100,
          required: true,
        }"
        label="Address Line 1"
        placeholder="Enter your detailed address, e.g., place name, street, etc."
        type="textarea"
        autogrow
        required
        :disable="isGstRegistered || disabled"
      />
    </div>
  </div>
</template>

<script setup>
import { getPincodeDetails } from "src/api/address";

defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
});

const { user } = storeToRefs(useUserStore());
const { states, stateFilter } = useStateFilter();
const { cities, cityFilter, citiesLoading, initCityFilter } = useCityFilter();

const isPincodeLoading = ref(false);
const isChangedThroughPincode = ref(false);
const formData = defineModel({ type: Object });

const computedAddress = computed({
  get: () =>
    Array.isArray(unref(formData).address)
      ? unref(formData).address[0]
      : unref(formData).address || unref(formData).address_line,
  set: (value) => {
    unref(formData).address_line = value;

    unref(formData).address = value;
  },
});

const computedStateCode = computed({
  get: () => unref(formData).state_code || unref(formData).state_cd,
  set: (value) => {
    unref(formData).state_code = value;

    unref(formData).state_cd = value;
  },
});

const isGstRegistered = computed(
  () => unref(user)?.businessDetails?.is_gst_registered,
);

const onUpdatePincode = async () => {
  const { pincode } = unref(formData);

  if (pincode?.length === 6) {
    try {
      isPincodeLoading.value = true;

      const response = await getPincodeDetails(pincode);
      const postOffice = response?.data?.[0]?.PostOffice?.[0];

      if (postOffice) {
        isChangedThroughPincode.value = true;

        const stateName = postOffice.State;
        const state = states.value.find((s) => s.name === stateName);

        if (state) {
          unref(formData).state_cd = state.state_code;

          unref(formData).state_code = state.state_code;
        }

        unref(formData).city = postOffice.District;

        unref(formData).country = postOffice.Country;
      }
    } catch (error) {
      console.error("Error fetching pincode details:", error);
    } finally {
      isPincodeLoading.value = false;
    }
  }
};

const onCreateCity = (city, done) => {
  done(city);

  unref(formData).city = city;

  cities.value.push({ name: city, _id: null });
};

const onUpdateState = (newState) => {
  if (newState) {
    if (!isChangedThroughPincode.value) {
      unref(formData).city = "";
    }

    initCityFilter(newState);
  }

  isChangedThroughPincode.value = false;
};
</script>
